import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, Plus, Sparkles, User, Users } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { resolveEventDisplayName } from '../../utils/eventDisplayName';
import { SURFACES } from '../../config/productSurfaces';
import { peopleListSurfaceClass } from '../../config/glassSurfaces';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../ui/Modal';
import Button from '../ui/Button';
import TextInput from '../ui/TextInput';
import {
  ModalSearchBar,
  ModalSearchEmptyState,
  ModalSearchInput,
  ModalSearchListSkeleton,
  ModalSearchResultRow,
  modalSearchScrollClassName,
} from '../search/ModalSearchField';
import { useNudge } from '../../lib/moments';

/**
 * The group's suggested name: the event's display name when it fits the field whole. A long one ("MAU P PRESENTS:
 * GENERATION REBR...") was prefilled cut off and had to be deleted (Sam, 2026-10-30), so it is left empty instead.
 */
function useSuggestedGroupName(eventId, eventName) {
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (eventName || !eventId) return undefined;
    let cancelled = false;
    supabase
      .from('events')
      .select('event_id, event_name, display_name, display_name_override')
      .eq('event_id', eventId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) setFetched(resolveEventDisplayName(data));
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, eventName]);
  const name = (eventName || fetched || '').trim();
  return name.length <= 28 ? name : '';
}

// Going with: the side picked last time starts selected (Friends the first time), so a repeat Join is one tap.
const SIDE_KEY = 'prii.going.side';
function readSide() {
  try {
    return window.localStorage.getItem(SIDE_KEY) === 'solo' ? 'solo' : 'friends';
  } catch {
    return 'friends';
  }
}
function rememberSide(side) {
  try {
    window.localStorage.setItem(SIDE_KEY, side);
  } catch {
    /* storage off: Friends next time */
  }
}
const SIDES = [
  { id: 'friends', label: 'Friends', Icon: Users },
  { id: 'solo', label: 'Just me', Icon: User },
];

function EmptyStateActionLink({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline font-medium text-primary underline underline-offset-2 hover:text-primary/90"
    >
      {children}
    </button>
  );
}

/**
 * Modal shown when a user RSVPs to an event — pick how to attend, or browse
 * existing groups for the event.
 *
 * Props:
 *   isOpen              - boolean
 *   onClose             - () => void
 *   onMatchMe           - () => void   (match queue; only rendered while SURFACES.matchMe)
 *   onCreateGroup       - (groupName: string) => void   (after the required "Group name" step)
 *   onSkip              - () => void
 *   onJoinExistingGroup - (groupId: string) => void | Promise<void>
 *   eventId             - UUID string for group search
 *   eventName           - optional string, shown on the choices step
 *   sharedInterestPanel - optional node; mutual-follow lineup interest shown on
 *                         the choices step (non-festival events)
 */
export default function GoingOptionsModal({
  isOpen,
  onClose,
  onMatchMe,
  onCreateGroup,
  onSkip,
  onJoinExistingGroup,
  eventId,
  eventName,
  sharedInterestPanel = null,
  initialStep = 'choices', // 'name' or 'search': the cards' Create a Group / Find a Group open straight to that step
}) {
  const groupGlow = useNudge('group'); // the first-time path (lib/moments): until the first group made or joined
  const [step, setStep] = useState('choices');
  const [searchQuery, setSearchQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [joiningGroupId, setJoiningGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [side, setSide] = useState(readSide);
  // Friends + Continue opens the event's groups while they load; if none turn out joinable, the name step instead.
  const viaContinueRef = useRef(false);
  const suggestedName = useSuggestedGroupName(eventId, eventName);
  useEffect(() => {
    if (step === 'name' && !groupName.trim() && suggestedName) setGroupName(suggestedName);
  }, [step, suggestedName]); // eslint-disable-line react-hooks/exhaustive-deps -- fills an empty field once per entry to the step

  const resetSearchState = useCallback(() => {
    setStep('choices');
    setSearchQuery('');
    setGroups([]);
    setGroupsLoading(false);
    setGroupsError(null);
    setJoiningGroupId(null);
    setGroupName('');
    viaContinueRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    resetSearchState();
    onClose?.();
  }, [onClose, resetSearchState]);

  useEffect(() => {
    if (!isOpen) {
      resetSearchState();
    } else {
      setStep(initialStep || 'choices');
      setSide(readSide());
    }
  }, [isOpen, resetSearchState, initialStep]);

  // The event's groups load when the modal opens, not only on Find: "Find a Group" shows only when there is a group you could
  // join (Sam, 2026-10-20) - otherwise it is Create a Group or Going solo.
  useEffect(() => {
    if (!isOpen || !eventId) return;

    let cancelled = false;
    setGroupsLoading(true);
    setGroupsError(null);

    (async () => {
      const { data, error } = await supabase.rpc('list_event_groups', {
        p_event_id: eventId,
      });
      if (cancelled) return;
      if (error) {
        console.error('[GoingOptionsModal] list_event_groups error:', error);
        setGroupsError('Could not load groups. Please try again.');
        setGroups([]);
      } else {
        setGroups((data ?? []).filter((g) => !g.is_member));
      }
      setGroupsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId]);

  // Joinable: not yours, not already requested. While the list loads Find stays hidden rather than flashing in and out.
  const canFindGroup =
    !groupsLoading && !groupsError && groups.some((g) => !g.has_pending_request && !g.is_full);

  useEffect(() => {
    if (step !== 'search' || !viaContinueRef.current || groupsLoading) return;
    viaContinueRef.current = false;
    if (!canFindGroup) setStep('name');
  }, [step, groupsLoading, canFindGroup]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, searchQuery]);

  const handleJoinRow = async (group) => {
    if (joiningGroupId || group.is_full || group.has_pending_request) return;
    setJoiningGroupId(group.id);
    try {
      await onJoinExistingGroup?.(group.id);
    } finally {
      setJoiningGroupId(null);
    }
  };

  if (!isOpen) return null;

  // The creator names the group - still the required last step (Sam, 2026-09-28: no more "Group 1"), but since
  // 2026-10-04 the field comes filled with the event's name when it fits (useSuggestedGroupName): most people have one group per event, so one tap on
  // Create is the whole step, and typing replaces the suggestion. The name goes to create_private_group(event, name).
  if (step === 'name') {
    const trimmed = groupName.trim();
    const submit = (e) => {
      e?.preventDefault?.();
      if (!trimmed) return;
      onCreateGroup?.(trimmed);
    };
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="sm" showClose={false}>
        <ModalHeader onClose={handleClose}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStep('choices')}
              aria-label="Back"
              className="shrink-0 -ml-1 p-2 rounded-full text-ink/70 hover:text-ink hover:bg-white/10 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg font-semibold text-ink leading-snug flex-1 min-w-0">
              New group
            </h2>
          </div>
        </ModalHeader>
        <form onSubmit={submit}>
          <ModalBody>
            {/* The field says what to do, and what a group is goes under it (Sam's B, 2026-11-04: the sheet never said
                to name the group) */}
            <label htmlFor="new-group-name" className="mb-1.5 block text-sm font-semibold text-ink">
              Name your group
            </label>
            <TextInput
              id="new-group-name"
              autoFocus
              onFocus={(e) => e.target.select()}
              type="text"
              placeholder="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={40}
              enterKeyHint="done"
              autoCapitalize="words"
              autoComplete="off"
              className="w-full"
            />
            <p className="mt-2 text-sm text-ink/70">
              Friends see each other on the map and share set times.
            </p>
          </ModalBody>
          <ModalFooter align="stack">
            <Button type="submit" fullWidth disabled={!trimmed}>
              Create group
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  }

  if (step === 'search') {
    const hasAnyGroups = groups.length > 0;
    const trimmedQuery = searchQuery.trim();

    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="md" showClose={false}>
        <ModalHeader onClose={handleClose}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setStep('choices');
                setSearchQuery('');
              }}
              aria-label="Back"
              className="shrink-0 -ml-1 p-2 rounded-full text-ink/70 hover:text-ink hover:bg-white/10 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg font-semibold text-ink leading-snug flex-1 min-w-0">Groups</h2>
          </div>
        </ModalHeader>

        <ModalSearchBar>
          <ModalSearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search groups..."
            autoFocus
          />
        </ModalSearchBar>

        <ModalBody className="flex min-h-0 flex-1 flex-col !p-0 max-h-[50vh]">
          <div className={modalSearchScrollClassName}>
            {groupsLoading ? (
              <ModalSearchListSkeleton count={4} showAvatar={false} />
            ) : groupsError ? (
              <ModalSearchEmptyState>{groupsError}</ModalSearchEmptyState>
            ) : filteredGroups.length === 0 ? (
              <ModalSearchEmptyState>
                {!hasAnyGroups ? (
                  <div className="space-y-2">
                    <p>No groups yet for this event.</p>
                    <p className="text-sm">
                      {SURFACES.matchMe && (
                        <>
                          Try{' '}
                          <EmptyStateActionLink onClick={onMatchMe}>Match me</EmptyStateActionLink>{' '}
                          to get paired, or{' '}
                        </>
                      )}
                      <EmptyStateActionLink onClick={() => setStep('name')}>
                        Create a Group
                      </EmptyStateActionLink>{' '}
                      and invite friends.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p>No groups match &quot;{trimmedQuery}&quot;.</p>
                    <p className="text-sm">
                      Try{' '}
                      {SURFACES.matchMe && (
                        <>
                          <EmptyStateActionLink onClick={onMatchMe}>Match me</EmptyStateActionLink>{' '}
                          or{' '}
                        </>
                      )}
                      <EmptyStateActionLink onClick={() => setStep('name')}>
                        Create a Group
                      </EmptyStateActionLink>
                      .
                    </p>
                  </div>
                )}
              </ModalSearchEmptyState>
            ) : (
              <div className={peopleListSurfaceClass()}>
                {filteredGroups.map((group) => {
                  const isPublic = (group.access_type || 'private') === 'public';
                  const actionLabel = isPublic ? 'Join' : 'Request to join';
                  const isDisabled =
                    group.is_full || group.has_pending_request || joiningGroupId === group.id;
                  const disabledLabel = group.has_pending_request
                    ? 'Request pending'
                    : group.is_full
                      ? 'Full'
                      : actionLabel;

                  return (
                    <ModalSearchResultRow key={group.id}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-dark-text glass-surface-text-emphasis">
                          {group.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted glass-surface-subtle-text">
                          <span className="glass-surface-muted-chip">
                            {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                            {!isPublic ? ' · Private' : ''}
                          </span>
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={isPublic ? 'primary' : 'secondary'}
                        size="sm"
                        disabled={isDisabled}
                        loading={joiningGroupId === group.id}
                        onClick={() => handleJoinRow(group)}
                        className="shrink-0 !min-w-[7.5rem]"
                      >
                        {isDisabled && joiningGroupId !== group.id ? disabledLabel : actionLabel}
                      </Button>
                    </ModalSearchResultRow>
                  );
                })}
                {/* Your own, at the end of the list: Friends + Continue lands here when there are groups to join */}
                <ModalSearchResultRow>
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-[var(--glass-accent-ring)] text-purple-200"
                    aria-hidden="true"
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-dark-text glass-surface-text-emphasis">
                      New group
                    </p>
                    {suggestedName && (
                      <p className="truncate text-xs text-gray-500 dark:text-dark-muted glass-surface-subtle-text">
                        {suggestedName}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setStep('name')}
                    className="shrink-0 !min-w-[7.5rem]"
                  >
                    Create
                  </Button>
                </ModalSearchResultRow>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
    );
  }

  // Going with (Sam's pick, 2026-11-01: Partiful's two big choices, built from the app's own glass buttons): two tall
  // glass tiles, the chosen one in the selected glass with the purple check, then Continue. Friends opens the event's
  // groups to join (with your own at the end) or, when there are none, the name; Just me is done, with a toast.
  const continueChoice = () => {
    rememberSide(side);
    if (side === 'solo') {
      onSkip?.();
      return;
    }
    viaContinueRef.current = true;
    setStep(groupsLoading || canFindGroup ? 'search' : 'name');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Going with" size="sm">
      <ModalBody>
        <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Going with">
          {SIDES.map(({ id, label, Icon }) => {
            const on = side === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setSide(id)}
                className={`flex min-h-[116px] flex-col items-center justify-center gap-2.5 rounded-btn font-semibold ${on ? 'btn-glass-accent' : 'btn-glass-neutral'}`}
              >
                <Icon className="h-7 w-7" aria-hidden="true" />
                <span>{label}</span>
                {on && (
                  // inline position: the glass button lifts its children with position: relative
                  <span
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    className="grid h-6 w-6 place-items-center rounded-full border-[1.5px] border-[var(--glass-accent-ring)] bg-[image:var(--glass-accent-fill)] text-white"
                    aria-hidden="true"
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {sharedInterestPanel}
      </ModalBody>
      <ModalFooter align="stack">
        {/* Match me is deferred with the rest of the social layer (SURFACES.matchMe, 2026-09-22). */}
        {SURFACES.matchMe && (
          <Button
            fullWidth
            variant="secondary"
            onClick={onMatchMe}
            leftIcon={<Sparkles className="h-4 w-4" />}
          >
            Match me
          </Button>
        )}
        <Button fullWidth onClick={continueChoice} className={side === 'friends' ? groupGlow : ''}>
          Continue
        </Button>
      </ModalFooter>
    </Modal>
  );
}
